import React from 'react';
import { Chip, ChipGroup } from '../components/ui/Chip';
import { SearchButton } from '../components/ui/SearchField';
import type { LocationItem, LocationId } from './types';

interface Props {
  locations: LocationItem[];        // team groups
  sites: string[];                  // declared physical sites (collector config)
  current: LocationId;
  onSelect: (id: LocationId) => void;
  onManage: () => void;
  onSearch: () => void;
}

/** Phone-friendly chip row: "My Chargers" (personal pins), then declared sites,
 *  then the user's pinned team groups, a Groups-manager affordance, and a Search
 *  entry.
 *
 *  Site chips are not the old HQ auto-bucket. That one was a catch-all for
 *  anything nobody had curated, which is why it kept surfacing customer units.
 *  A site is the opposite: a positive claim, backed by the address the charger's
 *  VPN connection arrived from. Everything else still shows only when pinned or
 *  grouped. */
export default function LocationPicker({ locations, sites, current, onSelect, onManage, onSearch }: Props) {
  // Pinned groups + the current group even if unpinned (resume-on-open can
  // restore a since-unpinned group — it must still have a chip to tap back to).
  const groups = locations.filter((l) => l.pinned || l.id === current);

  return (
    <ChipGroup label="Chargers" className="!flex-nowrap gap-2 overflow-x-auto pb-1 -mx-1 px-1">
      <Chip pressed={current === 'mine'} onClick={() => onSelect('mine')}>My Chargers</Chip>

      {sites.map((site) => {
        const id = `site:${site}` as LocationId;
        return (
          <Chip
            key={id} pressed={current === id} onClick={() => onSelect(id)}
            // Map pin: a site is a physical place, a group is a list somebody made.
            leading={
              <svg width="11" height="11" viewBox="0 0 12 16" fill="none" stroke="currentColor"
                strokeWidth="1.6" aria-hidden="true" className="flex-shrink-0">
                <path d="M6 15s4.5-5.2 4.5-9A4.5 4.5 0 0 0 1.5 6c0 3.8 4.5 9 4.5 9z" strokeLinejoin="round" />
                <circle cx="6" cy="6" r="1.6" />
              </svg>
            }
          >
            {site}
          </Chip>
        );
      })}

      {groups.map((loc) => (
        <Chip
          key={loc.id} pressed={loc.id === current} onClick={() => onSelect(loc.id)}
          count={loc.member_count}
          leading={loc.color ? <span className="h-2 w-2 rounded-full" style={{ backgroundColor: loc.color }} /> : undefined}
        >
          {loc.name}
        </Chip>
      ))}

      <button
        type="button" onClick={onManage}
        className="flex-shrink-0 px-3 py-2 rounded-full text-sm border border-dashed border-outline-variant text-on-surface-variant hover:text-on-surface hover:border-outline transition"
      >Groups</button>

      <SearchButton label="Search chargers" onClick={onSearch} className="ml-auto flex-none">Search</SearchButton>
    </ChipGroup>
  );
}
